import {Prose, Resource} from '@raison/types';
import {cn, RootLayout, RsnBublikChart, SkeletonContextProvider} from '@raison/ui-kit';
import * as Array from 'effect/Array';
import React, {createContext, useContext, useMemo} from 'react';
import {DashboardCategoryShortLine, DashboardCategoryTotal} from '~/entities/dashboard-categories';
import {LoadProblemPane} from '~/entities/fallbacks';
import * as css from './DashboardCategoryScreen.css';

export interface DashboardCategoryContext {
    color: string;
    kindPlural: Prose;
    kindSingular: Prose;
}
export const DashboardCategoryContext = createContext<DashboardCategoryContext | null>(null);

export function useDashboardCategoryContext() {
    const ctx = useContext(DashboardCategoryContext);
    if (!ctx) {
        throw new Error('DashboardCategoryContext not provided');
    }
    return useMemo(() => {
        const getKindName = (count: number) => (count === 1 ? ctx.kindSingular : ctx.kindPlural);
        return {...ctx, getKindName};
    }, Object.values(ctx));
}

export namespace DashboardCategoryScreen {
    interface PropsBase {
        title: string;
        Bublick?: React.ComponentType<{total: DashboardCategoryTotal}>;
        Heading?: React.ComponentType<{total: DashboardCategoryTotal}>;
    }

    export interface Props<TAsset> extends PropsBase {
        Item: React.ComponentType<{asset: TAsset}>;
        ready: boolean;
        total: DashboardCategoryTotal;
        assets: Groups<TAsset>;
    }

    export interface PropsOfAwaiting<TAsset, p extends Props<TAsset> = Props<TAsset>> extends PropsBase {
        Item: p['Item'] & PropsOfSkeleton['Item'];
        total: Resource<p['total']>;
        assets: Resource<p['assets']>;
        refresh: () => void;
    }

    export interface PropsOfSkeleton {
        Item: {Skeleton: React.ComponentType};
        title: string;
        errored: boolean;
        refresh: () => void;
    }

    type Groups<TAsset> = Array<{
        title: Prose | null;
        children: TAsset[];
    }>;
}

export function DashboardCategoryScreen<TAccount>(props: DashboardCategoryScreen.Props<TAccount>) {
    const {getKindName} = useDashboardCategoryContext();
    const count = props.assets.reduce((sum, g) => sum + g.children.length, 0);
    let groups = props.assets;
    if (count < 1) {
        groups = [];
    }
    return (
        <Layout
            ready={props.ready}
            title={props.title}
            chart={props.Bublick && <props.Bublick total={props.total} />}
            heading={props.Heading && <props.Heading total={props.total} />}
        >
            {groups.map((group, index) => (
                <section key={index} className={`flex flex-col ${index === 0 ? 'pt-2' : ''}`}>
                    {group.title != null && (
                        <h3 className={'fv-heading-s px-4 pb-2 pt-4 font-bold text-neutral-100'}>{group.title}</h3>
                    )}
                    {group.children.map((asset, index) => (
                        <props.Item key={index} asset={asset} />
                    ))}
                </section>
            ))}
            {count < 1 && (
                <div className='grid flex-1 place-items-center'>
                    <div className='fv-text-xl-regular text-neutral-60'>
                        No {getKindName(0)} available in this category yet
                    </div>
                </div>
            )}
        </Layout>
    );
}

DashboardCategoryScreen.Awaiting = function DashboardCategoryScreenAwaiting<TAsset>(
    props: DashboardCategoryScreen.PropsOfAwaiting<TAsset>
) {
    const resources = Resource.all(props.total, props.assets);
    if (!resources.isSuccess) {
        return DashboardCategoryScreen.Skeleton({
            Item: props.Item,
            title: props.title,
            refresh: props.refresh,
            errored: resources.isError,
        });
    }
    const [total, assets] = resources.data;
    return DashboardCategoryScreen({
        ...props,
        ready: resources.isSuccess,
        total,
        assets,
    });
};

DashboardCategoryScreen.Chart = function Chart({total: {balance, count, percent}}: {total: DashboardCategoryTotal}) {
    const {kindPlural, kindSingular, color} = useContext(DashboardCategoryContext)!;

    const kind = Intl.PluralRules().select(count) === 'one' ? kindSingular : kindPlural;
    return (
        <figure className={css.chart.pile}>
            <RsnBublikChart className={css.chart.bublick} parts={[{percent: percent, color}]} />{' '}
            <figcaption className={css.chart.caption}>
                <span>
                    {count} {kind}
                </span>
                <strong className={css.chart.title}>{balance}</strong>
            </figcaption>
        </figure>
    );
};

DashboardCategoryScreen.Heading = function Heading({total}: {total: DashboardCategoryTotal}) {
    return (
        <RootLayout.CustomHeader>
            <hgroup className='flex flex-col items-center gap-1'>
                <RootLayout.TitleOutlet />
                <small className='fv-text-m-regular whitespace-break-spaces text-center text-neutral-60'>
                    <DashboardCategoryShortLine totals={total} />
                </small>
            </hgroup>
        </RootLayout.CustomHeader>
    );
};

DashboardCategoryScreen.Skeleton = function Skeleton(props: DashboardCategoryScreen.PropsOfSkeleton) {
    return (
        <Layout title={props.title}>
            <SkeletonContextProvider value={{stopped: props.errored}}>
                {Array.makeBy(5, i => (
                    <props.Item.Skeleton key={i} />
                ))}
                {props.errored && <LoadProblemPane reset={props.refresh} />}
            </SkeletonContextProvider>
        </Layout>
    );
};

function Layout(
    props: React.PropsWithChildren<{
        title: string;
        chart?: React.JSX.Element;
        heading?: React.JSX.Element;
        ready?: boolean;
        empty?: boolean;
    }>
) {
    return props.chart ? renderWithChart() : renderNoChart();

    function renderNoChart() {
        return (
            <RootLayout title={props.title} pageHeader={props.heading}>
                {props.children}
            </RootLayout>
        );
    }

    function renderWithChart() {
        const cls = css.layoutWithChart;
        const empty = props.empty;
        const ready = props.ready && !empty;
        const height = 272;
        return (
            <RootLayout title={props.title} background={'none'}>
                {props.chart && (
                    <div style={{height}} className={cls.chart}>
                        {props.chart}
                    </div>
                )}
                <div className={cls.scroller}>
                    <div className={cls.spacer} style={{height: ready ? height : 0}} />
                    <div
                        className={cn(cls.contentBase, {
                            [cls.content.ready]: ready,
                            [cls.content.empty]: empty,
                            [cls.content.notEmpty]: !empty,
                        })}
                    >
                        {props.children}
                    </div>
                </div>
            </RootLayout>
        );
    }
}
